import type { IClientSubscribeOptions } from 'mqtt';

const UNSUBSCRIBE_DELAY_MS = 0;
export const WINCH_SUBSCRIBE_RETRY_MS = 2_000;
export const WINCH_SNAPSHOT_RETRY_MS = 2_000;
export const WINCH_SNAPSHOT_RETRY_LIMIT = 4;

const pendingTopicUnsubscribes = new Map<string, ReturnType<typeof setTimeout>>();

export function subscribeWinchTopics(
    topics: string[],
    subscribe: (
        topic: string,
        options?: IClientSubscribeOptions,
        onSettled?: (error?: Error | null) => void
    ) => void,
    onReady?: () => void
): () => void {
    let cancelled = false;
    const retryTimers = new Set<ReturnType<typeof setTimeout>>();
    let remaining = topics.length;
    const subscribeOne = (topic: string): void => {
        subscribe(topic, { qos: 1 }, (error) => {
            if (error) {
                if (cancelled) {
                    return;
                }
                const timer = setTimeout(() => {
                    retryTimers.delete(timer);
                    subscribeOne(topic);
                }, WINCH_SUBSCRIBE_RETRY_MS);
                retryTimers.add(timer);
                return;
            }
            // Strict-mode cleanup cancels this generation before SUBACK. Still report
            // ready: the replacement generation reads the latest snapshot callback.
            remaining -= 1;
            if (remaining <= 0) {
                onReady?.();
            }
        });
    };
    const cancel = (): void => {
        cancelled = true;
        for (const timer of retryTimers) {
            clearTimeout(timer);
        }
        retryTimers.clear();
    };
    if (topics.length === 0) {
        onReady?.();
        return cancel;
    }
    for (const topic of topics) {
        const pending = pendingTopicUnsubscribes.get(topic);
        if (pending != null) {
            clearTimeout(pending);
            pendingTopicUnsubscribes.delete(topic);
        }
        subscribeOne(topic);
    }
    return cancel;
}

/**
 * Delay unsubscribe so React Strict Mode remount can cancel it and keep the
 * subscription (retained `online` is not replayed on a duplicate subscribe).
 * A real unmount or cage change still unsubscribes on the next macrotask.
 */
export function scheduleUnsubscribeWinchTopics(
    topics: string[],
    unsubscribe: (topic: string) => void
): void {
    for (const topic of topics) {
        const existing = pendingTopicUnsubscribes.get(topic);
        if (existing != null) {
            clearTimeout(existing);
        }
        pendingTopicUnsubscribes.set(
            topic,
            setTimeout(() => {
                unsubscribe(topic);
                pendingTopicUnsubscribes.delete(topic);
            }, UNSUBSCRIBE_DELAY_MS)
        );
    }
}

/** Republish snapshots until the winch answers, then stop. A full page reload is not required. */
export function scheduleSnapshotRetries(
    publishSnapshots: () => void,
    hasHeard: () => boolean,
    onGiveUp: () => void
): () => void {
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = (): void => {
        timer = null;
        if (hasHeard()) {
            return;
        }
        attempts += 1;
        if (attempts > WINCH_SNAPSHOT_RETRY_LIMIT) {
            onGiveUp();
            return;
        }
        publishSnapshots();
        timer = setTimeout(tick, WINCH_SNAPSHOT_RETRY_MS);
    };
    timer = setTimeout(tick, WINCH_SNAPSHOT_RETRY_MS);
    return () => {
        if (timer != null) {
            clearTimeout(timer);
            timer = null;
        }
    };
}

export function clearPendingWinchUnsubscribes(): void {
    for (const timer of pendingTopicUnsubscribes.values()) {
        clearTimeout(timer);
    }
    pendingTopicUnsubscribes.clear();
}
